import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import { logout } from '../Store/slices/authSlice';
import BrandLogo from '../components/BrandLogo';
import StepTabs from '../components/StepTabs';
import StatusIcon from '../components/StatusIcon';
import showNotification from '../Utility Components/iFitNotification';
import {
  createOutletStepOneForPartner,
  createPartnerStepOne,
  cancelPartnerOnboarding,
  completePartnerOnboarding,
  deleteOutlet,
  editPartner,
  editOutlet,
  fetchAutomatedAlerts,
  sendQueuedAlerts,
  loadAutomatedAlertQueue,
  loadOutletBillingSubscription,
  loadOutlets,
  loadPartnerSettings,
  loadPlanChangeRequests,
  loadSubscriptionPlans,
  loadPartners,
  resendPartnerOtp,
  updateSuperAdminSettings,
  uploadOutletLogo,
  setOutletBillingSubscription,
  toggleOutlet,
  togglePartner,
  uploadOutletDocumentsForPartner,
  uploadPartnerDocuments,
  verifyPartnerOtp
} from '../Store/slices/superAdminWorkspaceSlice';

const COUNTRY_CONFIGS = {
  India: {
    dialCode: '+91',
    mobileLength: 10,
    pinLength: 6,
    states: {
      'Andhra Pradesh': [],
      'Arunachal Pradesh': [],
      Assam: [],
      Bihar: [],
      'Chhattisgarh': [],
      Goa: [],
      Gujarat: [],
      Haryana: [],
      'Himachal Pradesh': [],
      Jharkhand: [],
      Karnataka: ['Bengaluru', 'Mysuru'],
      Kerala: [],
      'Madhya Pradesh': [],
      Maharashtra: ['Mumbai', 'Pune'],
      Manipur: [],
      Meghalaya: [],
      Mizoram: [],
      Nagaland: [],
      Odisha: [],
      Punjab: [],
      Rajasthan: [],
      Sikkim: [],
      'Tamil Nadu': [],
      Telangana: [],
      Tripura: [],
      'Uttar Pradesh': [],
      Uttarakhand: [],
      'West Bengal': []
    }
  },
  'United States': {
    dialCode: '+1',
    mobileLength: 10,
    pinLength: 5,
    states: {
      California: ['Los Angeles', 'San Francisco'],
      Texas: ['Houston', 'Austin'],
      'New York': ['New York City', 'Buffalo']
    }
  }
};

const getCountryConfig = (country) => COUNTRY_CONFIGS[country] || null;

const emptyOutletForm = {
  name: '',
  logo: '',
  country: '',
  city: '',
  state: '',
  address: '',
  pincode: '',
  associated_partner: ''
};

const dateInputValue = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ALERT_TYPE_LABELS = {
  automated_before_due: 'Before due',
  automated_before_due_5: '5 days before',
  automated_before_due_3: '3 days before',
  automated_before_due_1: '1 day before',
  automated_due: 'Due today',
  automated_overdue: 'Overdue',
  automated_overdue_1: '1 day overdue',
  automated_overdue_2: '2 days overdue',
  auto_pre_due_1: 'Before due',
  auto_pre_due_2: 'Before due',
  auto_pre_due_3: 'Before due',
  auto_post_due_1: 'Overdue',
  auto_post_due_2: 'Overdue',
  auto_disconnection: 'Cancellation notice'
};

const SUPER_ADMIN_MENU = [
  { key: 'partners', label: 'Partners', icon: GroupsOutlinedIcon },
  { key: 'outlets', label: 'Outlets', icon: StorefrontOutlinedIcon },
  { key: 'plan-requests', label: 'Upgrade/Downgrade Requests', icon: QueryStatsOutlinedIcon },
  { key: 'alerts', label: 'Alerts', icon: NotificationsActiveOutlinedIcon },
  { key: 'settings', label: 'Settings', icon: AdminPanelSettingsOutlinedIcon }
];

const DEFAULT_SETTINGS_DRAFT = {
  email_verification_required: true,
  plan_charges: {
    starter: 599,
    growth: 1499
  },
  alert_schedules: {
    growth: {
      stages: [
        { type: 'automated_before_due_5', label: '5 days before', bucket: 'before_due', day_count: 5, offset_days: -5, enabled: true },
        { type: 'automated_before_due_3', label: '3 days before', bucket: 'before_due', day_count: 3, offset_days: -3, enabled: true },
        { type: 'automated_before_due_1', label: '1 day before', bucket: 'before_due', day_count: 1, offset_days: -1, enabled: true },
        { type: 'automated_due', label: 'Due date', bucket: 'on_due', day_count: '', offset_days: 0, enabled: true },
        { type: 'automated_overdue_1', label: '1 day after', bucket: 'after_due', day_count: 1, offset_days: 1, enabled: true },
        { type: 'automated_overdue_2', label: '2 days after', bucket: 'after_due', day_count: 2, offset_days: 2, enabled: true }
      ]
    }
  },
  alert_templates: {
    automated_before_due_5: 'Hi {member_name}, your {plan_name} payment for {outlet_name} is due on {due_date}. Amount due: {currency} {amount}.',
    automated_before_due_3: 'Hi {member_name}, your {plan_name} payment for {outlet_name} is due on {due_date}. Please renew in time.',
    automated_before_due_1: 'Hi {member_name}, your {plan_name} payment for {outlet_name} is due tomorrow. Please renew in time.',
    automated_due: 'Hi {member_name}, your {plan_name} payment for {outlet_name} is due today. Amount due: {currency} {amount}.',
    automated_overdue_1: 'Hi {member_name}, your {plan_name} payment for {outlet_name} was due on {due_date}. Please complete payment.',
    automated_overdue_2: 'Hi {member_name}, your payment remains overdue. Please clear {currency} {amount} for {outlet_name}.'
  }
};

const mergeSettingsDraft = (settings) => {
  const settingsWithoutDeliveryModes = { ...(settings || {}) };
  delete settingsWithoutDeliveryModes.delivery_modes;
  return {
    ...DEFAULT_SETTINGS_DRAFT,
    ...settingsWithoutDeliveryModes,
    plan_charges: {
      ...DEFAULT_SETTINGS_DRAFT.plan_charges,
      ...((settings || {}).plan_charges || {})
    },
    alert_schedules: {
      growth: {
        ...DEFAULT_SETTINGS_DRAFT.alert_schedules.growth,
        stages: DEFAULT_SETTINGS_DRAFT.alert_schedules.growth.stages
      },
    },
    alert_templates: {
      ...DEFAULT_SETTINGS_DRAFT.alert_templates,
      ...((settings || {}).alert_templates || {})
    }
  };
};

const envFlag = (value, defaultValue = true) => {
  if (value === undefined || value === null || value === '') {
    return defaultValue;
  }
  return ['1', 'true', 'yes', 'on'].includes(String(value).trim().toLowerCase());
};

function SuperAdminDashboard() {
  const dispatch = useDispatch();
  const { partners, outlets, outletBillingSubscriptions, subscriptionPlans, automatedAlertQueue, automatedAlertFetchSummary, planChangeRequests, settings, emailVerificationRequired, status, actionStatus, error } = useSelector((state) => state.superAdminWorkspace);
  const { name: adminName, email: adminEmail } = useSelector((state) => state.auth);
  console.log('in admin',process.env.REACT_APP_API_BASE_URL);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://app-server-fjekdtazayatetet.indiasouthcentral-01.azurewebsites.net/BarbellOS/api/1.0.0';
  const apiOrigin = useMemo(() => {
    try {
      return new URL(apiBaseUrl).origin;
    } catch (e) {
      return 'https://app-server-fjekdtazayatetet.indiasouthcentral-01.azurewebsites.net';
    }
  }, [apiBaseUrl]);
  const superadminReminderControlEnabled = envFlag(
    process.env.REACT_APP_REMINDER_SUPERADMIN_CONTROL_ENABLED ?? process.env.REACT_APP_SUPERADMIN_REMINDER_CONTROL_ENABLED,
    true
  );
  const [activeTab, setActiveTab] = useState('partners');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [step, setStep] = useState(1);
  const [createdPartnerId, setCreatedPartnerId] = useState('');
  const [verified, setVerified] = useState({ email: false });
  const [otp, setOtp] = useState({ email: '' });
  const [docType, setDocType] = useState('kyc');
  const [documents, setDocuments] = useState([]);
  const [skipPartnerDocumentUpload, setSkipPartnerDocumentUpload] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [editingPartnerStep, setEditingPartnerStep] = useState(1);
  const [editingPartnerDocType, setEditingPartnerDocType] = useState('kyc');
  const [editingPartnerDocuments, setEditingPartnerDocuments] = useState([]);
  const [editingPartnerOriginalMobile, setEditingPartnerOriginalMobile] = useState('');
  const [editingPartnerOtp, setEditingPartnerOtp] = useState('');
  const [editingPartnerOtpSent, setEditingPartnerOtpSent] = useState(false);
  const [editingPartnerOtpVerified, setEditingPartnerOtpVerified] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [outletSearch, setOutletSearch] = useState('');
  const [partnerFilterOutlet, setPartnerFilterOutlet] = useState('');
  const [partnerFilterStatus, setPartnerFilterStatus] = useState('ALL');
  const [partnerFilterCity, setPartnerFilterCity] = useState('');
  const [partnerSortOrder, setPartnerSortOrder] = useState('newest');
  const [outletFilterPartner, setOutletFilterPartner] = useState('');
  const [outletFilterStatus, setOutletFilterStatus] = useState('ALL');
  const [outletFilterCity, setOutletFilterCity] = useState('');
  const [outletSortOrder, setOutletSortOrder] = useState('newest');
  const [alertSearch, setAlertSearch] = useState('');
  const [alertDate, setAlertDate] = useState(() => dateInputValue());
  const [alertOutletFilter, setAlertOutletFilter] = useState('ALL');
  const [alertStageFilter, setAlertStageFilter] = useState('ALL');
  const [selectedAlertIds, setSelectedAlertIds] = useState([]);
  const [outletModal, setOutletModal] = useState({ type: null, outlet: null, title: '' });
  const [outletStep, setOutletStep] = useState(1);
  const [outletDocType, setOutletDocType] = useState('license');
  const [outletDocuments, setOutletDocuments] = useState([]);
  const [skipOutletDocumentUpload, setSkipOutletDocumentUpload] = useState(false);
  const [outletEditStep, setOutletEditStep] = useState(1);
  const [outletBillingTierSelection, setOutletBillingTierSelection] = useState({});
  const [settingsDraft, setSettingsDraft] = useState(DEFAULT_SETTINGS_DRAFT);
  const prevActionStatusRef = useRef(actionStatus);
  const prevErrorRef = useRef(error);

  const [partnerForm, setPartnerForm] = useState({
    name: '',
    email: '',
    mobile: '',
    upi_id: '',
    password: '',
    country: '',
    city: '',
    state: '',
    address: '',
    pin_code: ''
  });
  const [outletForm, setOutletForm] = useState(emptyOutletForm);
  const [outletLogoFile, setOutletLogoFile] = useState(null);
  const [outletLogoPreview, setOutletLogoPreview] = useState('');
  const partnerCountryConfig = useMemo(() => getCountryConfig(partnerForm.country), [partnerForm.country]);
  const partnerStateOptions = useMemo(
    () => Object.keys(partnerCountryConfig?.states || {}),
    [partnerCountryConfig]
  );
  const partnerCityOptions = useMemo(() => {
    const state = partnerForm.state;
    if (!state) {
      return [];
    }
    return partnerCountryConfig?.states?.[state] || [];
  }, [partnerCountryConfig, partnerForm.state]);
  const outletCountryConfig = useMemo(() => getCountryConfig(outletForm.country), [outletForm.country]);
  const outletStateOptions = useMemo(
    () => Object.keys(outletCountryConfig?.states || {}),
    [outletCountryConfig]
  );
  const outletCityOptions = useMemo(() => {
    const state = outletForm.state;
    if (!state) {
      return [];
    }
    return outletCountryConfig?.states?.[state] || [];
  }, [outletCountryConfig, outletForm.state]);

  const activeCount = useMemo(() => partners.filter((p) => p.is_active).length, [partners]);
  const partnersById = useMemo(() => {
    const map = {};
    partners.forEach((partner) => {
      map[String(partner.id)] = partner;
    });
    return map;
  }, [partners]);
  const outletsById = useMemo(() => {
    const map = {};
    outlets.forEach((outlet) => {
      map[String(outlet.id)] = outlet;
    });
    return map;
  }, [outlets]);

  const allPartnerCities = useMemo(() => {
    return Array.from(new Set(partners.map((p) => String(p.city || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [partners]);

  const allOutletCities = useMemo(() => {
    return Array.from(new Set(outlets.map((o) => String(o.city || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [outlets]);

  const filteredPartners = useMemo(() => {
    const term = partnerSearch.trim().toLowerCase();
    return partners
      .filter((partner) => {
        if (term && ![partner.name, partner.email, partner.mobile].some((value) => String(value || '').toLowerCase().includes(term))) {
          return false;
        }
        if (partnerFilterStatus && partnerFilterStatus !== 'ALL') {
          const statusLabel = getPartnerStatusLabel(partner);
          if (String(statusLabel || '').toUpperCase() !== String(partnerFilterStatus || '').toUpperCase()) {
            return false;
          }
        }
        if (partnerFilterCity) {
          if (!String(partner.city || '').toLowerCase().includes(String(partnerFilterCity || '').toLowerCase())) {
            return false;
          }
        }
        if (partnerFilterOutlet) {
          const outletTerm = String(partnerFilterOutlet || '').trim();
          if (outletTerm) {
            const matches = outlets.some((o) => {
              const idMatch = String(o.id) === outletTerm;
              const nameMatch = String(o.name || '').toLowerCase() === outletTerm.toLowerCase();
              return (idMatch || nameMatch) && String(o.associated_partner) === String(partner.id);
            });
            if (!matches) return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        if (!a.created_on && !b.created_on) return 0;
        const ta = a.created_on ? new Date(a.created_on).getTime() : 0;
        const tb = b.created_on ? new Date(b.created_on).getTime() : 0;
        return partnerSortOrder === 'newest' ? tb - ta : ta - tb;
      });
  }, [partners, partnerSearch, partnerFilterOutlet, partnerFilterStatus, partnerFilterCity, partnerSortOrder, outlets]);

  const filteredOutlets = useMemo(() => {
    const term = outletSearch.trim().toLowerCase();
    return outlets
      .filter((outlet) => {
        const partner = partnersById[String(outlet.associated_partner)];
        if (term) {
          const found = [
            outlet.name,
            outlet.country,
            outlet.city,
            outlet.state,
            outlet.pincode,
            outlet.address,
            partner?.name,
            partner?.email,
            partner?.mobile
          ].some((value) => String(value || '').toLowerCase().includes(term));
          if (!found) return false;
        }
        if (outletFilterStatus && outletFilterStatus !== 'ALL') {
          const statusLabel = outlet.is_active ? 'Active' : 'Inactive';
          if (String(statusLabel || '').toUpperCase() !== String(outletFilterStatus || '').toUpperCase()) return false;
        }
        if (outletFilterCity) {
          if (!String(outlet.city || '').toLowerCase().includes(String(outletFilterCity || '').toLowerCase())) return false;
        }
        if (outletFilterPartner) {
          const pterm = String(outletFilterPartner || '').trim();
          if (pterm) {
            const partnerMatch = String(outlet.associated_partner) === pterm || String(partner?.name || '').toLowerCase() === pterm.toLowerCase();
            if (!partnerMatch) return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        if (!a.created_on && !b.created_on) return 0;
        const ta = a.created_on ? new Date(a.created_on).getTime() : 0;
        const tb = b.created_on ? new Date(b.created_on).getTime() : 0;
        return outletSortOrder === 'newest' ? tb - ta : ta - tb;
      });
  }, [outlets, outletSearch, partnersById, outletFilterPartner, outletFilterStatus, outletFilterCity, outletSortOrder]);
  const filteredAlertQueue = useMemo(() => {
    const term = alertSearch.trim().toLowerCase();
    return (automatedAlertQueue || []).filter((item) => {
      const scheduledDate = item?.scheduled_for ? dateInputValue(new Date(item.scheduled_for)) : '';
      const statusMatches = String(item?.delivery_status || '').toLowerCase() === 'queued';
      const dateMatches = scheduledDate === alertDate;
      const searchMatches = !term || [
        item?.delivery_status,
        'email',
        item?.alert_type,
        item?.member_name,
        item?.outlet_name,
        item?.partner_name,
        item?.queued_by_name,
        item?.processed_by_name,
        item?.message_text
      ].some((value) => String(value || '').toLowerCase().includes(term));
      const outletMatches = alertOutletFilter === 'ALL' || String(item?.outlet_id || '') === alertOutletFilter;
      const stageMatches = alertStageFilter === 'ALL' || String(item?.alert_type || '') === alertStageFilter;
      return statusMatches && dateMatches && searchMatches && outletMatches && stageMatches;
    });
  }, [automatedAlertQueue, alertDate, alertSearch, alertOutletFilter, alertStageFilter]);
  const outletCountsByPartnerId = useMemo(() => {
    const counts = {};
    outlets.forEach((outlet) => {
      const partnerId = outlet.associated_partner ? String(outlet.associated_partner) : '';
      if (!partnerId) {
        return;
      }
      counts[partnerId] = (counts[partnerId] || 0) + 1;
    });
    return counts;
  }, [outlets]);
  const isEditingMobileChanged = useMemo(() => {
    if (!editingPartner) {
      return false;
    }
    return String(editingPartner.mobile || '').trim() !== String(editingPartnerOriginalMobile || '').trim();
  }, [editingPartner, editingPartnerOriginalMobile]);
  const getPartnerStatusLabel = (partner) => {
    if (!partner) {
      return 'Unknown';
    }
    if (!partner.is_active) {
      return 'Inactive';
    }
    if (emailVerificationRequired && !partner.is_email_verified) {
      return 'Email pending';
    }
    if (!partner.is_mobile_verified) {
      return 'Mobile pending';
    }
    if (!partner.is_document_uploaded) {
      return 'Docs pending';
    }
    if (!partner.is_outlet_added) {
      return 'Outlet pending';
    }
    return 'Active';
  };
  const formatDocUrl = (path) => {
    if (!path) {
      return '';
    }
    const normalized = String(path).replace(/\\/g, '/');
    if (/^https?:\/\//i.test(normalized)) {
      return normalized;
    }
    const withLeadingSlash = normalized.startsWith('/') ? normalized : `/${normalized}`;
    return `${apiOrigin}${withLeadingSlash}`;
  };

  const tierOptions = useMemo(() => {
    if (!Array.isArray(subscriptionPlans) || subscriptionPlans.length === 0) {
      return [
        { tier: 'starter', label: 'Starter' },
        { tier: 'growth', label: 'Growth' }
      ];
    }
    return subscriptionPlans.map((plan) => {
      const tier = String(plan.tier || '').toLowerCase();
      const freeCredits = Number(plan.free_monthly_credits || 0);
      const creditLabel = freeCredits > 0 ? `${freeCredits} free credits/mo` : 'No free credits';
      const label = `${plan.display_name || tier.toUpperCase()} (${creditLabel})`;
      return { tier, label };
    });
  }, [subscriptionPlans]);

  const pendingPartnerCount = partners.filter((partner) => getPartnerStatusLabel(partner) !== 'Active').length;
  const activeOutletCount = outlets.filter((outlet) => outlet.is_active).length;
  const inactiveOutletCount = Math.max(outlets.length - activeOutletCount, 0);
  const automatedQueuedAlertCount = (automatedAlertQueue || []).filter((item) => String(item?.delivery_status || '').toLowerCase() === 'queued').length;
  const scheduledOutletCount = new Set(filteredAlertQueue.map((alert) => String(alert.outlet_id || alert.outlet_name || 'unassigned'))).size;
  const adminDisplayName = adminName || adminEmail || 'Super Admin';
  const adminInitial = (adminDisplayName || 'A').trim().charAt(0).toUpperCase();
  const activeTabLabel = SUPER_ADMIN_MENU.find((item) => item.key === activeTab)?.label || 'Workspace';
  const activeSearchValue = activeTab === 'settings' ? '' : activeTab === 'outlets' ? outletSearch : activeTab === 'alerts' ? alertSearch : partnerSearch;
  const activeSearchPlaceholder = activeTab === 'outlets'
    ? 'Search outlets, partners, cities...'
    : activeTab === 'alerts'
      ? 'Search alerts, members, messages...'
      : activeTab === 'settings'
        ? 'Settings'
      : 'Search partners, emails, mobiles...';
  const getStatusKind = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'active' || normalized === 'sent') {
      return 'active';
    }
    if (normalized === 'failed') {
      return 'failed';
    }
    if (normalized === 'skipped' || normalized === 'passed') {
      return 'warning';
    }
    return 'inactive';
  };
  const formatAlertType = (value) => ALERT_TYPE_LABELS[String(value || '').toLowerCase()] || String(value || '-').replace(/_/g, ' ');
  const formatAlertChannel = (value) => {
    return 'EMAIL';
  };
  const getAlertLimitLabel = (alert) => alert?.channel_limit_label || (alert?.channel_limit === 0 ? 'Unlimited' : (alert?.channel_limit ?? 0));
  const handleAdminSearch = (value) => {
    if (activeTab === 'settings') {
      return;
    }
    if (activeTab === 'outlets') {
      setOutletSearch(value);
      return;
    }
    if (activeTab === 'alerts') {
      setAlertSearch(value);
      return;
    }
    setPartnerSearch(value);
  };
  const refreshActiveTab = () => {
    if (activeTab === 'plan-requests') {
      dispatch(loadPlanChangeRequests());
      return;
    }
    if (activeTab === 'outlets') {
      dispatch(loadOutlets());
      dispatch(loadSubscriptionPlans());
      return;
    }
    if (activeTab === 'alerts') {
      dispatch(loadAutomatedAlertQueue());
      return;
    }
    if (activeTab === 'settings') {
      dispatch(loadPartnerSettings());
      dispatch(loadSubscriptionPlans());
      return;
    }
    dispatch(loadPartners());
    dispatch(loadOutlets());
  };

  useEffect(() => {
    dispatch(loadPartners());
    dispatch(loadOutlets());
    dispatch(loadPartnerSettings());
    dispatch(loadSubscriptionPlans());
    dispatch(loadAutomatedAlertQueue());
    dispatch(loadPlanChangeRequests());
  }, [dispatch]);

  // Debounced server-side filter for partner city
  useEffect(() => {
    const timer = setTimeout(() => {
      if (partnerFilterCity) {
        dispatch(loadPartners({ city: partnerFilterCity }));
      } else {
        dispatch(loadPartners());
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [partnerFilterCity, dispatch]);

  // Debounced server-side filter for outlet city
  useEffect(() => {
    const timer = setTimeout(() => {
      if (outletFilterCity) {
        dispatch(loadOutlets({ city: outletFilterCity }));
      } else {
        dispatch(loadOutlets());
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [outletFilterCity, dispatch]);

  useEffect(() => {
    outlets.forEach((outlet) => {
      dispatch(loadOutletBillingSubscription(String(outlet.id)));
    });
  }, [outlets, dispatch]);

  useEffect(() => {
    if (error && error !== prevErrorRef.current) {
      showNotification('error', 'topRight', 'Action failed', String(error));
    }
    prevErrorRef.current = error;
  }, [error]);

  useEffect(() => {
    if (prevActionStatusRef.current !== 'succeeded' && actionStatus === 'succeeded') {
      showNotification('success', 'topRight', 'Success', 'Operation completed successfully.');
    }
    prevActionStatusRef.current = actionStatus;
  }, [actionStatus]);

  useEffect(() => {
    setSettingsDraft(mergeSettingsDraft(settings));
  }, [settings]);

  const updatePlanChargeDraft = (tier, value) => {
    setSettingsDraft((prev) => ({
      ...prev,
      plan_charges: {
        ...prev.plan_charges,
        [tier]: value
      }
    }));
  };

  const updateScheduleStageDraft = (tier, index, field, value) => {
    setSettingsDraft((prev) => {
      const stages = [...(prev.alert_schedules?.[tier]?.stages || [])];
      const current = { ...stages[index] };
      if (field === 'bucket') {
        current.bucket = value;
        if (value === 'on_due') {
          current.day_count = '';
          current.offset_days = 0;
        } else {
          const days = Math.max(Number(current.day_count || Math.abs(current.offset_days || 1) || 1), 1);
          current.day_count = days;
          current.offset_days = value === 'before_due' ? -days : days;
        }
      } else if (field === 'day_count') {
        const days = value === '' ? '' : Math.max(Number(value || 0), 0);
        current.day_count = current.bucket === 'on_due' ? '' : days;
        current.offset_days = current.bucket === 'on_due' ? 0 : current.bucket === 'before_due' ? -Number(days || 0) : Number(days || 0);
      } else {
        current[field] = value;
      }
      stages[index] = {
        ...current
      };
      return {
        ...prev,
        alert_schedules: {
          ...prev.alert_schedules,
          [tier]: {
            ...(prev.alert_schedules?.[tier] || {}),
            stages
          }
        }
      };
    });
  };

  const updateAlertTemplateDraft = (alertType, value) => {
    setSettingsDraft((prev) => ({
      ...prev,
      alert_templates: {
        ...(prev.alert_templates || {}),
        [alertType]: value
      }
    }));
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    const normalizeStage = (stage) => {
      const bucket = stage.bucket || 'before_due';
      const rawDayCount = stage.day_count ?? Math.abs(stage.offset_days || 0);
      const dayCount = bucket === 'on_due' ? '' : Math.max(Number(rawDayCount || 0), 0);
      return {
        ...stage,
        bucket,
        day_count: dayCount,
        offset_days: bucket === 'on_due' ? 0 : bucket === 'before_due' ? -Number(dayCount || 0) : Number(dayCount || 0)
      };
    };
    const payload = {
      ...settingsDraft,
      plan_charges: Object.fromEntries(
        Object.entries(settingsDraft.plan_charges || {}).map(([tier, value]) => [tier, Math.max(Number(value || 0), 0)])
      ),
      alert_schedules: {
        growth: {
          ...(settingsDraft.alert_schedules?.growth || {}),
          stages: (settingsDraft.alert_schedules?.growth?.stages || []).map(normalizeStage)
        },
      }
    };
    await dispatch(updateSuperAdminSettings(payload));
  };

  const startOnboarding = () => {
    setShowOnboarding(true);
    setStep(1);
    setCreatedPartnerId('');
    setVerified({ email: false });
    setOtp({ email: '' });
    setDocType('kyc');
    setDocuments([]);
    setSkipPartnerDocumentUpload(false);
    setPartnerForm({
      name: '',
      email: '',
      mobile: '',
      upi_id: '',
      password: '',
      country: '',
      city: '',
      state: '',
      address: '',
      pin_code: ''
    });
  };

  const cancelOnboarding = async () => {
    if (createdPartnerId) {
      const result = await dispatch(cancelPartnerOnboarding({ userId: createdPartnerId }));
      if (!cancelPartnerOnboarding.fulfilled.match(result)) {
        return;
      }
    }
    setShowOnboarding(false);
    setStep(1);
    setCreatedPartnerId('');
    setDocuments([]);
    setSkipPartnerDocumentUpload(false);
  };

  const submitStepOne = async (event) => {
    event.preventDefault();
    const errors = [];
    const name = String(partnerForm.name || '').trim();
    if (!name) {
      errors.push('Name is required.');
    } else if (name.length > 20) {
      errors.push('Name must be 20 characters or fewer.');
    }
    const emailValue = String(partnerForm.email || '').trim();
    if (!emailValue) {
      errors.push('Email is required.');
    }
    const countryValue = String(partnerForm.country || '').trim();
    if (!countryValue) {
      errors.push('Country is required before mobile validation.');
    }
    const digits = String(partnerForm.mobile || '').replace(/\D/g, '');
    if (!digits) {
      errors.push('Mobile number is required.');
    } else if (partnerCountryConfig?.mobileLength && digits.length !== partnerCountryConfig.mobileLength) {
      errors.push(`Mobile number must be ${partnerCountryConfig.mobileLength} digits for ${partnerForm.country}.`);
    }
    const addressValue = String(partnerForm.address || '').trim();
    if (!addressValue || addressValue.length < 5) {
      errors.push('Address must be at least 5 characters.');
    }
    const pinValue = String(partnerForm.pin_code || '').trim();
    if (!pinValue) {
      errors.push('Pin code is required.');
    } else if (partnerCountryConfig?.pinLength && pinValue.length !== partnerCountryConfig.pinLength) {
      errors.push(`Pin code must be ${partnerCountryConfig.pinLength} digits for ${partnerForm.country}.`);
    } else if (!/^\d{5,6}$/.test(pinValue)) {
      errors.push('Pin code must be 5-6 digits.');
    }
    if (errors.length > 0) {
      showNotification('error', 'topRight', 'Validation Error', errors.join(' '));
      return;
    }
    const result = await dispatch(createPartnerStepOne({ ...partnerForm, role_id: 2 }));
    if (createPartnerStepOne.fulfilled.match(result)) {
      const userId = result.payload?.id;
      if (userId) {
        setCreatedPartnerId(String(userId));
        setStep(2);
      }
    }
  };

  const submitOtp = async (medium) => {
    if (!createdPartnerId || !otp[medium]) {
      return;
    }
    const result = await dispatch(
      verifyPartnerOtp({
        user_id: createdPartnerId,
        otp: otp[medium],
        medium
      })
    );
    if (verifyPartnerOtp.fulfilled.match(result)) {
      setVerified({ email: true });
      setStep(3);
    }
  };

  const submitStepThree = async (event) => {
    event.preventDefault();
    if (!createdPartnerId) {
      showNotification('error', 'topRight', 'Partner unavailable', 'Create the partner before finishing onboarding.');
      return;
    }
    if (documents.length === 0) {
      if (skipPartnerDocumentUpload) {
        const result = await dispatch(completePartnerOnboarding({ userId: createdPartnerId }));
        if (completePartnerOnboarding.fulfilled.match(result)) {
          setShowOnboarding(false);
          setStep(1);
          setDocuments([]);
          setSkipPartnerDocumentUpload(false);
          showNotification('success', 'topRight', 'Onboarding completed', 'Partner documents can be uploaded later from Edit Partner.');
        }
        return;
      }
      showNotification('error', 'topRight', 'Documents required', 'Upload partner documents or select Upload later.');
      return;
    }
    const result = await dispatch(
      completePartnerOnboarding({
        files: documents,
        userId: createdPartnerId,
        docType
      })
    );
    if (completePartnerOnboarding.fulfilled.match(result)) {
      setShowOnboarding(false);
      setStep(1);
      setDocuments([]);
      setSkipPartnerDocumentUpload(false);
    }
  };

  const submitEdit = async (event) => {
    event.preventDefault();
    if (!editingPartner) {
      return;
    }
    const editErrors = [];
    const trimmedName = String(editingPartner.name || '').trim();
    const trimmedEmail = String(editingPartner.email || '').trim();
    const trimmedMobile = String(editingPartner.mobile || '').trim();
    if (trimmedName && trimmedName.length > 20) {
      editErrors.push('Name must be 20 characters or fewer.');
    }
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      editErrors.push('Email format is invalid.');
    }
    if (trimmedMobile && !/^\+?\d{6,15}$/.test(trimmedMobile.replace(/\s/g, ''))) {
      editErrors.push('Mobile must be 6-15 digits.');
    }
    if (editErrors.length > 0) {
      showNotification('error', 'topRight', 'Validation Error', editErrors.join(' '));
      return;
    }
    const payload = {
      name: trimmedName ? trimmedName : null,
      email: trimmedEmail ? trimmedEmail : null,
      mobile: trimmedMobile ? trimmedMobile : null,
      otp: null,
      otp_medium: null
    };
    const result = await dispatch(editPartner({ partnerId: editingPartner.id, details: payload }));
    if (editPartner.fulfilled.match(result)) {
      setEditingPartnerStep(2);
      setEditingPartnerOtp('');
      setEditingPartnerOtpSent(false);
      setEditingPartnerOtpVerified(false);
    }
  };

  const verifyEditMobileOtp = async () => {
    if (!editingPartner || !editingPartnerOtp) {
      return;
    }
    const result = await dispatch(
      verifyPartnerOtp({
        user_id: editingPartner.id,
        otp: editingPartnerOtp,
        medium: 'email'
      })
    );
    if (verifyPartnerOtp.fulfilled.match(result)) {
      setEditingPartnerOtpVerified(true);
      showNotification('success', 'topRight', 'Verified', 'Email OTP verified.');
    }
  };

  const handleLogoUpload = (file) => {
    if (!file) {
      return;
    }
    if (outletLogoPreview) {
      URL.revokeObjectURL(outletLogoPreview);
    }
    setOutletLogoFile(file);
    setOutletLogoPreview(URL.createObjectURL(file));
  };

  const sendEditMobileOtp = async () => {
    if (!editingPartner) {
      return;
    }
    const sendResult = await dispatch(resendPartnerOtp({ user_id: editingPartner.id, medium: 'email' }));
    if (resendPartnerOtp.fulfilled.match(sendResult)) {
      setEditingPartnerOtpSent(true);
      showNotification('success', 'topRight', 'OTP Sent', 'OTP sent to email.');
    }
  };

  const submitEditPartnerDocuments = async (event) => {
    event.preventDefault();
    if (!editingPartner?.id || editingPartnerDocuments.length === 0) {
      return;
    }
    const result = await dispatch(
      uploadPartnerDocuments({
        files: editingPartnerDocuments,
        userId: editingPartner.id,
        docType: editingPartnerDocType
      })
    );
    if (uploadPartnerDocuments.fulfilled.match(result)) {
      setEditingPartner(null);
      setEditingPartnerStep(1);
      setEditingPartnerDocType('kyc');
      setEditingPartnerDocuments([]);
    }
  };

  const closeOutletModal = () => {
    setOutletModal({ type: null, outlet: null, title: '' });
    setOutletForm(emptyOutletForm);
    setOutletStep(1);
    setOutletEditStep(1);
    setOutletDocType('license');
    setOutletDocuments([]);
    setSkipOutletDocumentUpload(false);
    if (outletLogoPreview) {
      URL.revokeObjectURL(outletLogoPreview);
    }
    setOutletLogoFile(null);
    setOutletLogoPreview('');
  };

  const validateOutletStepOne = () => {
    const errors = [];
    const name = String(outletForm.name || '').trim();
    if (!name) {
      errors.push('Outlet name is required.');
    }
    const countryValue = String(outletForm.country || '').trim();
    if (!countryValue) {
      errors.push('Country is required.');
    }
    const stateValue = String(outletForm.state || '').trim();
    if (!stateValue) {
      errors.push('State is required.');
    }
    const cityValue = String(outletForm.city || '').trim();
    if (!cityValue) {
      errors.push('City is required.');
    }
    const addressValue = String(outletForm.address || '').trim();
    if (!addressValue || addressValue.length < 5) {
      errors.push('Address must be at least 5 characters.');
    }
    const pinValue = String(outletForm.pincode || '').trim();
    if (!pinValue) {
      errors.push('Pin code is required.');
    } else if (outletCountryConfig?.pinLength && pinValue.length !== outletCountryConfig.pinLength) {
      errors.push(`Pin code must be ${outletCountryConfig.pinLength} digits for ${outletForm.country}.`);
    } else if (!/^\d{5,6}$/.test(pinValue)) {
      errors.push('Pin code must be 5-6 digits.');
    }
    if (errors.length > 0) {
      showNotification('error', 'topRight', 'Validation Error', errors.join(' '));
      return false;
    }
    return true;
  };

  const openCreateOutletModal = (partnerId = '') => {
    setOutletForm({ ...emptyOutletForm, associated_partner: partnerId });
    setOutletStep(1);
    setOutletDocType('license');
    setOutletDocuments([]);
    setSkipOutletDocumentUpload(false);
    if (outletLogoPreview) {
      URL.revokeObjectURL(outletLogoPreview);
    }
    setOutletLogoFile(null);
    setOutletLogoPreview('');
    setOutletModal({ type: 'create', outlet: null, title: 'Add Outlet' });
  };

  const openEditOutletModal = (outlet) => {
    setOutletForm({
      name: outlet.name || '',
      logo: outlet.logo || '',
      country: outlet.country || '',
      city: outlet.city || '',
      state: outlet.state || '',
      address: outlet.address || '',
      pincode: outlet.pincode || '',
      associated_partner: outlet.associated_partner ? String(outlet.associated_partner) : ''
    });
    setOutletModal({ type: 'edit', outlet, title: 'Edit Outlet' });
    setOutletEditStep(1);
    setOutletDocType('license');
    setOutletDocuments([]);
    setSkipOutletDocumentUpload(false);
    if (outletLogoPreview) {
      URL.revokeObjectURL(outletLogoPreview);
    }
    setOutletLogoFile(null);
    setOutletLogoPreview(outlet.logo ? formatDocUrl(outlet.logo) : '');
  };

  const openToggleOutletModal = (outlet) => {
    setOutletModal({
      type: 'toggle',
      outlet,
      title: `${outlet.is_active ? 'Disable' : 'Enable'} Outlet`
    });
  };

  const openDeleteOutletModal = (outlet) => {
    setOutletModal({ type: 'delete', outlet, title: 'Delete Outlet' });
  };

  const runAutomatedFetch = async () => {
    if (!superadminReminderControlEnabled) {
      showNotification('warning', 'topRight', 'View-only mode', 'Reminder sending is handled by the backend daily job.');
      return;
    }
    const result = await dispatch(fetchAutomatedAlerts({
      target_date: alertDate,
      window_days: 0,
      include_overdue: false
    }));
    if (fetchAutomatedAlerts.fulfilled.match(result)) {
      const data = result.payload || {};
      showNotification(
        'success',
        'topRight',
        'Reminder list ready',
        `${data.created || 0} reminders added and ${data.existing || 0} already queued for ${alertDate}.`
      );
    }
  };

  const toggleSelectedAlert = (alertId) => {
    setSelectedAlertIds((previous) => previous.includes(alertId)
      ? previous.filter((id) => id !== alertId)
      : [...previous, alertId]);
  };

  const queuedVisibleAlertIds = filteredAlertQueue
    .filter((alert) => String(alert?.delivery_status || '').toLowerCase() === 'queued')
    .map((alert) => alert.id);
  const toggleSelectAllAlerts = () => {
    setSelectedAlertIds((previous) => {
      const allSelected = queuedVisibleAlertIds.length > 0 && queuedVisibleAlertIds.every((id) => previous.includes(id));
      return allSelected ? previous.filter((id) => !queuedVisibleAlertIds.includes(id)) : [...new Set([...previous, ...queuedVisibleAlertIds])];
    });
  };

  const sendSelectedAlerts = async () => {
    if (!selectedAlertIds.length) return;
    const result = await dispatch(sendQueuedAlerts(selectedAlertIds));
    if (sendQueuedAlerts.fulfilled.match(result)) {
      setSelectedAlertIds([]);
      showNotification('success', 'topRight', 'Emails processed', `${result.payload?.sent || 0} emails sent.`);
    }
  };

  const submitOutletModal = async (event) => {
    event.preventDefault();
    if (!outletModal.type) {
      return;
    }

    if (outletModal.type === 'create') {
      if (outletStep === 1) {
        if (!validateOutletStepOne()) {
          return;
        }
        setOutletStep(2);
        return;
      }
      if (!skipOutletDocumentUpload && outletDocuments.length === 0) {
        showNotification('error', 'topRight', 'Documents required', 'Upload outlet documents or select Upload later.');
        return;
      }
      const result = await dispatch(
        createOutletStepOneForPartner({
          ...outletForm,
          logo: outletForm.logo || null,
          associated_partner: outletForm.associated_partner
        })
      );
      if (!createOutletStepOneForPartner.fulfilled.match(result)) {
        return;
      }
      const outletId = result.payload?.id;
      if (!outletId) {
        showNotification('error', 'topRight', 'Outlet not saved', 'Outlet was not created because the server did not return an outlet ID.');
        return;
      }
      if (outletLogoFile) {
        await dispatch(uploadOutletLogo({ outletId, file: outletLogoFile }));
      }
      if (skipOutletDocumentUpload) {
        closeOutletModal();
        return;
      }
      const uploadResult = await dispatch(
        uploadOutletDocumentsForPartner({
          files: outletDocuments,
          outletId,
          docType: outletDocType
        })
      );
      if (uploadOutletDocumentsForPartner.fulfilled.match(uploadResult)) {
        closeOutletModal();
        return;
      }
      await dispatch(deleteOutlet({ outletId }));
      return;
    }

    if (!outletModal.outlet?.id) {
      return;
    }

    if (outletModal.type === 'edit') {
      if (outletEditStep === 1) {
        if (!validateOutletStepOne()) {
          return;
        }
        const result = await dispatch(
          editOutlet({
            outletId: outletModal.outlet.id,
            details: {
              ...outletForm,
              logo: outletForm.logo || null,
              associated_partner: outletForm.associated_partner
            }
          })
        );
        if (editOutlet.fulfilled.match(result)) {
          if (outletLogoFile) {
            await dispatch(uploadOutletLogo({ outletId: outletModal.outlet.id, file: outletLogoFile }));
          }
          setOutletEditStep(2);
        }
        return;
      }
      if (skipOutletDocumentUpload) {
        closeOutletModal();
        return;
      }
      if (outletDocuments.length === 0) {
        showNotification('error', 'topRight', 'Documents required', 'Upload outlet documents or select Upload later.');
        return;
      }
      const uploadResult = await dispatch(
        uploadOutletDocumentsForPartner({
          files: outletDocuments,
          outletId: outletModal.outlet.id,
          docType: outletDocType
        })
      );
      if (uploadOutletDocumentsForPartner.fulfilled.match(uploadResult)) {
        closeOutletModal();
      }
      return;
    }

    if (outletModal.type === 'toggle') {
      const result = await dispatch(
        toggleOutlet({
          outletId: outletModal.outlet.id,
          activate: !outletModal.outlet.is_active
        })
      );
      if (toggleOutlet.fulfilled.match(result)) {
        closeOutletModal();
      }
      return;
    }

    if (outletModal.type === 'delete') {
      const result = await dispatch(deleteOutlet({ outletId: outletModal.outlet.id }));
      if (deleteOutlet.fulfilled.match(result)) {
        closeOutletModal();
      }
    }
  };

  return (
    <main className="partner-layout">
      <header className="partner-header admin-header">
        <div className="partner-header__brand">
          <BrandLogo compact />
          <span className="partner-header__workspace">Super Admin Workspace</span>
        </div>
        <label className="partner-search">
          <SearchOutlinedIcon fontSize="small" />
          <input
            value={activeSearchValue}
            onChange={(event) => handleAdminSearch(event.target.value)}
            placeholder={activeSearchPlaceholder}
            aria-label={`Search ${activeTabLabel.toLowerCase()}`}
            disabled={activeTab === 'settings'}
          />
        </label>
        <div className="header-actions admin-header-actions" aria-label="Admin workspace controls">
          <button
            className="header-icon-btn header-icon-btn--notify"
            type="button"
            title="Alert queue"
            aria-label={`Alert queue${automatedQueuedAlertCount ? `, ${automatedQueuedAlertCount} queued` : ''}`}
            onClick={() => setActiveTab('alerts')}
          >
            <NotificationsNoneOutlinedIcon fontSize="small" />
            {automatedQueuedAlertCount > 0 ? <span className="header-icon-btn__badge">{automatedQueuedAlertCount}</span> : null}
          </button>
          <div className="partner-user-card" aria-label={`Signed in as ${adminDisplayName}`}>
            <span className="partner-user-card__avatar" aria-hidden="true">{adminInitial}</span>
            <div className="partner-user-card__copy">
              <span>Signed in as</span>
              <strong>{adminDisplayName}</strong>
            </div>
          </div>
          <button className="header-icon-btn header-icon-btn--refresh" type="button" onClick={refreshActiveTab} title={`Refresh ${activeTabLabel}`} aria-label={`Refresh ${activeTabLabel}`}>
            <RefreshOutlinedIcon fontSize="small" />
          </button>
          <button className="header-icon-btn header-icon-btn--logout" type="button" onClick={() => dispatch(logout())} title="Logout" aria-label="Logout">
            <LogoutOutlinedIcon fontSize="small" />
          </button>
        </div>
      </header>

      <section className={`partner-shell${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        <aside className={`partner-sidebar${sidebarCollapsed ? ' is-collapsed' : ''}`}>
          <button
            className="sidebar-collapse-btn"
            type="button"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!sidebarCollapsed}
            onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
          >
            {sidebarCollapsed ? <ChevronRightOutlinedIcon fontSize="small" /> : <ChevronLeftOutlinedIcon fontSize="small" />}
          </button>
          <div className="sidebar-nav">
            <p className="sidebar-nav-label">Manage</p>
            {SUPER_ADMIN_MENU.filter((item) => ['partners', 'outlets'].includes(item.key)).map((item) => (
              <button
                key={item.key}
                type="button"
                title={item.label}
                className={`side-btn ${activeTab === item.key ? 'active' : ''}`}
                onClick={() => setActiveTab(item.key)}
              >
                <span className="side-btn-content">
                  <span className="side-btn-icon"><item.icon fontSize="small" /></span>
                  <span className="side-btn-label">{item.label}</span>
                </span>
              </button>
            ))}
            <p className="sidebar-nav-label">Operations</p>
            {SUPER_ADMIN_MENU.filter((item) => ['plan-requests', 'alerts', 'settings'].includes(item.key)).map((item) => (
              <button
                key={item.key}
                type="button"
                title={item.label}
                className={`side-btn ${activeTab === item.key ? 'active' : ''}`}
                onClick={() => setActiveTab(item.key)}
              >
                <span className="side-btn-content">
                  <span className="side-btn-icon"><item.icon fontSize="small" /></span>
                  <span className="side-btn-label">{item.label}</span>
                </span>
              </button>
            ))}
          </div>
          <div className="sidebar-insight-card">
            <QueryStatsOutlinedIcon fontSize="small" />
            <strong>Control center</strong>
            <span>Review partners, outlets, and queued alerts from one workspace.</span>
            <button className="sidebar-insight-card__link" type="button" onClick={() => setActiveTab('alerts')}>
              View Alerts
            </button>
          </div>
        </aside>

        <section className="partner-main">
          {activeTab === 'partners' ? (
            <article className="panel workspace-page admin-workspace-page">
              <div className="workspace-page-header">
                <div>
                  <h2>Partners</h2>
                  <p>Manage partner accounts, onboarding status, and outlet access.</p>
                </div>
                <button className="btn-link btn-add" type="button" onClick={startOnboarding}>
                  <AddCircleOutlineOutlinedIcon fontSize="small" />
                  Add Partner
                </button>
              </div>

              <section className="workspace-stats-grid" aria-label="Partner summary">
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__icon"><GroupsOutlinedIcon fontSize="small" /></span>
                  <strong>{partners.length}</strong>
                  <span>Total Partners</span>
                </article>
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__dot is-active" />
                  <strong>{activeCount}</strong>
                  <span>Active Partners</span>
                </article>
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__dot is-inactive" />
                  <strong>{pendingPartnerCount}</strong>
                  <span>Pending Review</span>
                </article>
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__icon is-purple"><StorefrontOutlinedIcon fontSize="small" /></span>
                  <strong>{outlets.length}</strong>
                  <span>Total Outlets</span>
                </article>
              </section>

              <div className="section-filter-row" style={{gap: '0.6rem', marginTop: '0.6rem'}}>
                <input
                  list="admin-outlet-list"
                  className="outlet-filter"
                  placeholder="Filter by outlet (searchable)"
                  value={partnerFilterOutlet}
                  onChange={(e) => setPartnerFilterOutlet(e.target.value)}
                />
                <datalist id="admin-outlet-list">
                  {outlets.map((o) => (
                    <option key={o.id} value={o.name}>{o.name}</option>
                  ))}
                </datalist>

                <select className="outlet-filter" value={partnerFilterStatus} onChange={(e) => setPartnerFilterStatus(e.target.value)}>
                  <option value="ALL">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Email pending">Email pending</option>
                  <option value="Mobile pending">Mobile pending</option>
                  <option value="Docs pending">Docs pending</option>
                  <option value="Outlet pending">Outlet pending</option>
                </select>

                <input list="admin-partner-city-list" className="outlet-filter" placeholder="City" value={partnerFilterCity} onChange={(e) => setPartnerFilterCity(e.target.value)} />
                <datalist id="admin-partner-city-list">
                  {allPartnerCities.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </datalist>

                <select className="outlet-filter" value={partnerSortOrder} onChange={(e) => setPartnerSortOrder(e.target.value)}>
                  <option value="newest">Sort: Newest</option>
                  <option value="oldest">Sort: Oldest</option>
                </select>
              </div>

              {status === 'loading' ? <p className="muted">Loading partners...</p> : null}
              <div className="table-wrap workspace-table-card">
                <table className="pro-table mobile-cards table-admin-partners">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Status</th>
                      <th># of Outlets</th>
                      <th>Address</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPartners.length === 0 ? (
                      <tr>
                        <td colSpan="7">No partners found.</td>
                      </tr>
                    ) : (
                      filteredPartners.map((partner) => {
                        const statusLabel = getPartnerStatusLabel(partner);
                        return (
                          <tr key={partner.id}>
                            <td>
                              <div className="outlet-name-cell">
                                <span className="outlet-name-cell__icon"><AdminPanelSettingsOutlinedIcon fontSize="small" /></span>
                                <span>
                                  <strong>{partner.name || '-'}</strong>
                                  <small>ID: {String(partner.id || '-').slice(0, 8)}</small>
                                </span>
                              </div>
                            </td>
                            <td>{partner.email || '-'}</td>
                            <td>{partner.mobile || '-'}</td>
                            <td><StatusIcon active={getStatusKind(statusLabel) === 'active'} /></td>
                            <td>{outletCountsByPartnerId[String(partner.id)] || 0}</td>
                            <td>
                              {[partner.address, partner.city, partner.state].filter(Boolean).join(', ') || '-'}
                            </td>
                            <td>
                              <div className="row-actions">
                                <button
                                  type="button"
                                  className={`btn-link ${partner.is_active ? 'btn-disable' : 'btn-enable'} mini`}
                                  disabled={actionStatus === 'loading'}
                                  onClick={() =>
                                    dispatch(togglePartner({ partnerId: partner.id, activate: !partner.is_active }))
                                  }
                                >
                                  {partner.is_active ? <BlockOutlinedIcon fontSize="small" /> : <CheckCircleOutlineOutlinedIcon fontSize="small" />}
                                  {partner.is_active ? 'Disable' : 'Enable'}
                                </button>
                                <button
                                  type="button"
                                  className="btn-link btn-edit mini"
                                  onClick={() => {
                                    setEditingPartner({
                                      id: partner.id,
                                      name: partner.name || '',
                                      email: partner.email || '',
                                      mobile: partner.mobile || '',
                                      document_path: partner.document_path || ''
                                    });
                                    setEditingPartnerOriginalMobile(partner.mobile || '');
                                    setEditingPartnerOtp('');
                                    setEditingPartnerOtpSent(false);
                                    setEditingPartnerOtpVerified(false);
                                    setEditingPartnerStep(1);
                                    setEditingPartnerDocType('kyc');
                                    setEditingPartnerDocuments([]);
                                  }}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                  Edit
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
                <div className="workspace-table-footer">
                  <span>Showing {filteredPartners.length} of {partners.length} partners</span>
                  <span className="workspace-table-footer__pager">1</span>
                </div>
              </div>
            </article>
          ) : null}

          {activeTab === 'outlets' ? (
            <article className="panel workspace-page admin-workspace-page">
              <div className="workspace-page-header">
                <div>
                  <h2>Outlets</h2>
                  <p>Review outlet ownership, billing tiers, and operational status.</p>
                </div>
                <button className="btn-link btn-add" type="button" onClick={() => openCreateOutletModal()}>
                  <AddCircleOutlineOutlinedIcon fontSize="small" />
                  Add Outlet
                </button>
              </div>

              <section className="workspace-stats-grid" aria-label="Outlet summary">
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__icon"><StorefrontOutlinedIcon fontSize="small" /></span>
                  <strong>{outlets.length}</strong>
                  <span>Total Outlets</span>
                </article>
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__dot is-active" />
                  <strong>{activeOutletCount}</strong>
                  <span>Active Outlets</span>
                </article>
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__dot is-inactive" />
                  <strong>{inactiveOutletCount}</strong>
                  <span>Inactive Outlets</span>
                </article>
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__icon is-purple"><GroupsOutlinedIcon fontSize="small" /></span>
                  <strong>{partners.length}</strong>
                  <span>Partners</span>
                </article>
              </section>

              <div className="section-filter-row" style={{gap: '0.6rem', marginTop: '0.6rem'}}>
                <input
                  list="admin-partner-list"
                  className="outlet-filter"
                  placeholder="Filter by partner (searchable)"
                  value={outletFilterPartner}
                  onChange={(e) => setOutletFilterPartner(e.target.value)}
                />
                <datalist id="admin-partner-list">
                  {partners.map((p) => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                </datalist>

                <select className="outlet-filter" value={outletFilterStatus} onChange={(e) => setOutletFilterStatus(e.target.value)}>
                  <option value="ALL">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>

                <input list="admin-outlet-city-list" className="outlet-filter" placeholder="City" value={outletFilterCity} onChange={(e) => setOutletFilterCity(e.target.value)} />
                <datalist id="admin-outlet-city-list">
                  {allOutletCities.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </datalist>

                <select className="outlet-filter" value={outletSortOrder} onChange={(e) => setOutletSortOrder(e.target.value)}>
                  <option value="newest">Sort: Newest</option>
                  <option value="oldest">Sort: Oldest</option>
                </select>
              </div>

              {status === 'loading' ? <p className="muted">Loading outlets...</p> : null}
              <div className="table-wrap workspace-table-card admin-outlets-table">
                <div className="admin-outlet-card-list">
                  {filteredOutlets.length === 0 ? (
                    <article className="admin-outlet-card admin-outlet-card--empty">No outlets found.</article>
                  ) : (
                    filteredOutlets.map((outlet) => {
                      const partner = partnersById[String(outlet.associated_partner)];
                      const subscription = outletBillingSubscriptions[String(outlet.id)];
                      const selectedTier = outletBillingTierSelection[String(outlet.id)] || subscription?.tier || '';
                      const memberCount =
                        outlet.member_count ?? outlet.members_count ?? outlet.memberCount ?? outlet.membersCount ?? '-';
                      const outletName = outlet.name || 'Outlet';
                      const createdDate = outlet.created_on ? new Date(outlet.created_on).toLocaleDateString() : '-';
                      return (
                        <article className="admin-outlet-card" key={`mobile-${outlet.id}`}>
                          <div className="admin-outlet-card__top">
                            <div className="admin-outlet-card__identity">
                              <span className="admin-outlet-card__eyebrow">Outlet</span>
                              <h3>{outletName}</h3>
                              <small>ID: {String(outlet.id || outlet.pincode || '-').slice(0, 8)}</small>
                            </div>
                            <StatusIcon active={outlet.is_active} />
                          </div>

                          <div className="admin-outlet-card__grid">
                            <div className="admin-outlet-card__field">
                              <span>Partner</span>
                              <strong>{partner?.name || '-'}</strong>
                              <small>{[partner?.mobile, partner?.email].filter(Boolean).join(' | ') || 'No contact'}</small>
                            </div>
                            <div className="admin-outlet-card__field">
                              <span>Members</span>
                              <strong>{memberCount}</strong>
                            </div>
                            <div className="admin-outlet-card__field">
                              <span>Created</span>
                              <strong>{createdDate}</strong>
                            </div>
                            <div className="admin-outlet-card__field admin-outlet-card__field--wide">
                              <span>Address</span>
                              <p>{outlet.address || '-'}</p>
                            </div>
                          </div>

                          <div className="admin-outlet-card__plan">
                            <span>Plan</span>
                            <div className="admin-plan-control">
                              <select
                                className="outlet-filter"
                                value={selectedTier}
                                onChange={(event) =>
                                  setOutletBillingTierSelection((prev) => ({
                                    ...prev,
                                    [String(outlet.id)]: event.target.value
                                  }))
                                }
                              >
                                <option value="">Select Tier</option>
                                {tierOptions.map((option) => (
                                  <option key={option.tier} value={option.tier}>{option.label}</option>
                                ))}
                              </select>
                              <button
                                className="btn-link btn-save mini"
                                type="button"
                                disabled={!selectedTier || actionStatus === 'loading'}
                                title={`Save billing plan for ${outletName}`}
                                aria-label={`Save billing plan for ${outletName}`}
                                onClick={() =>
                                  dispatch(
                                    setOutletBillingSubscription({
                                      outletId: String(outlet.id),
                                      tier: selectedTier
                                    })
                                  )
                                }
                              >
                                <SaveOutlinedIcon fontSize="small" />
                                Save
                              </button>
                            </div>
                          </div>

                          <div className="row-actions admin-table-actions admin-outlet-card__actions">
                            <button
                              className="btn-link btn-edit mini"
                              type="button"
                              title={`Edit ${outletName}`}
                              aria-label={`Edit ${outletName}`}
                              onClick={() => openEditOutletModal(outlet)}
                            >
                              <EditOutlinedIcon fontSize="small" />
                              Edit
                            </button>
                            <button
                              className={`btn-link ${outlet.is_active ? 'btn-disable' : 'btn-enable'} mini`}
                              type="button"
                              title={`${outlet.is_active ? 'Disable' : 'Enable'} ${outletName}`}
                              aria-label={`${outlet.is_active ? 'Disable' : 'Enable'} ${outletName}`}
                              onClick={() => openToggleOutletModal(outlet)}
                            >
                              {outlet.is_active ? <BlockOutlinedIcon fontSize="small" /> : <CheckCircleOutlineOutlinedIcon fontSize="small" />}
                              {outlet.is_active ? 'Disable' : 'Enable'}
                            </button>
                              <button
                                className="btn-link btn-delete mini"
                                type="button"
                                data-click-guard="off"
                                title={`Delete ${outletName}`}
                                aria-label={`Delete ${outletName}`}
                                onClick={() => openDeleteOutletModal(outlet)}
                            >
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                              Delete
                            </button>
                          </div>
                        </article>
                      );
                    })
                  )}
                </div>
                <table className="pro-table mobile-cards table-admin-outlets">
                  <thead>
                    <tr>
                      <th>Outlet</th>
                      <th>Due Date</th>
                      <th>Address</th>
                      <th>Status</th>
                      <th>Plan</th>
                      <th>Members</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOutlets.length === 0 ? (
                      <tr>
                        <td colSpan="8">No outlets found.</td>
                      </tr>
                    ) : (
                      filteredOutlets.map((outlet) => {
                        const partner = partnersById[String(outlet.associated_partner)];
                        const subscription = outletBillingSubscriptions[String(outlet.id)];
                        const selectedTier = outletBillingTierSelection[String(outlet.id)] || subscription?.tier || '';
                        const memberCount =
                          outlet.member_count ?? outlet.members_count ?? outlet.memberCount ?? outlet.membersCount ?? '-';
                        return (
                        <tr key={outlet.id}>
                          <td>
                            <div className="outlet-name-cell">
                              <span className="outlet-name-cell__icon"><StorefrontOutlinedIcon fontSize="small" /></span>
                              <span>
                                <strong>{outlet.name || '-'}</strong>
                                <small>ID: {String(outlet.id || outlet.pincode || '-').slice(0, 8)}</small>
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="admin-contact-cell">
                              <strong>{partner?.name || '-'}</strong>
                              <small>{[partner?.mobile, partner?.email].filter(Boolean).join(' | ') || 'No contact'}</small>
                            </div>
                          </td>
                          <td><span className="admin-muted-cell">{outlet.address || '-'}</span></td>
                          <td><StatusIcon active={outlet.is_active} /></td>
                          <td>
                            <div className="admin-plan-control">
                              <select
                                className="outlet-filter"
                                value={selectedTier}
                                onChange={(event) =>
                                  setOutletBillingTierSelection((prev) => ({
                                    ...prev,
                                    [String(outlet.id)]: event.target.value
                                  }))
                                }
                              >
                                <option value="">Select Tier</option>
                                {tierOptions.map((option) => (
                                  <option key={option.tier} value={option.tier}>{option.label}</option>
                                ))}
                              </select>
                              <button
                                className="btn-link btn-save mini"
                                type="button"
                                disabled={!selectedTier || actionStatus === 'loading'}
                                title={`Save billing plan for ${outlet.name || 'outlet'}`}
                                aria-label={`Save billing plan for ${outlet.name || 'outlet'}`}
                                onClick={() =>
                                  dispatch(
                                    setOutletBillingSubscription({
                                      outletId: String(outlet.id),
                                      tier: selectedTier
                                    })
                                  )
                                }
                              >
                                <SaveOutlinedIcon fontSize="small" />
                                Save
                              </button>
                            </div>
                          </td>
                          <td>{memberCount}</td>
                          <td>{outlet.created_on ? new Date(outlet.created_on).toLocaleDateString() : '-'}</td>
                          <td>
                            <div className="row-actions admin-table-actions">
                              <button
                                className="btn-link btn-edit mini"
                                type="button"
                                title={`Edit ${outlet.name || 'outlet'}`}
                                aria-label={`Edit ${outlet.name || 'outlet'}`}
                                onClick={() => openEditOutletModal(outlet)}
                              >
                                <EditOutlinedIcon fontSize="small" />
                                Edit
                              </button>
                              <button
                                className={`btn-link ${outlet.is_active ? 'btn-disable' : 'btn-enable'} mini`}
                                type="button"
                                title={`${outlet.is_active ? 'Disable' : 'Enable'} ${outlet.name || 'outlet'}`}
                                aria-label={`${outlet.is_active ? 'Disable' : 'Enable'} ${outlet.name || 'outlet'}`}
                                onClick={() => openToggleOutletModal(outlet)}
                              >
                                {outlet.is_active ? <BlockOutlinedIcon fontSize="small" /> : <CheckCircleOutlineOutlinedIcon fontSize="small" />}
                                {outlet.is_active ? 'Disable' : 'Enable'}
                              </button>
                              <button
                                className="btn-link btn-delete mini"
                                type="button"
                                data-click-guard="off"
                                title={`Delete ${outlet.name || 'outlet'}`}
                                aria-label={`Delete ${outlet.name || 'outlet'}`}
                                onClick={() => openDeleteOutletModal(outlet)}
                              >
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      )})
                    )}
                  </tbody>
                </table>
                <div className="workspace-table-footer">
                  <span>Showing {filteredOutlets.length} of {outlets.length} outlets</span>
                  <span className="workspace-table-footer__pager">1</span>
                </div>
              </div>
            </article>
          ) : null}

          {activeTab === 'plan-requests' ? (
            <article className="panel workspace-page admin-workspace-page">
              <div className="workspace-page-header">
                <div>
                  <h2>Upgrade/Downgrade Requests</h2>
                  <p>Plan change requests submitted by partners. No email is sent when a request is created.</p>
                </div>
                <button className="btn-link btn-save" type="button" onClick={() => dispatch(loadPlanChangeRequests())}>
                  <RefreshOutlinedIcon fontSize="small" /> Refresh
                </button>
              </div>
              <div className="table-wrap workspace-table-card">
                <table className="pro-table mobile-cards">
                  <thead><tr><th>Partner</th><th>Outlet</th><th>Old Plan</th><th>New Plan</th><th>Request</th><th>Request Date</th><th>Status</th></tr></thead>
                  <tbody>
                    {(planChangeRequests || []).length === 0 ? <tr><td colSpan="7">No upgrade or downgrade requests yet.</td></tr> : planChangeRequests.map((request) => (
                      <tr key={request.id}>
                        <td>{request.partner_name}</td><td>{request.outlet_name}</td>
                        <td>{String(request.old_plan || '-').replace(/^./, (value) => value.toUpperCase())}</td>
                        <td>{String(request.new_plan || '-').replace(/^./, (value) => value.toUpperCase())}</td>
                        <td>{request.request_type === 'downgrade' ? 'Downgrade' : 'Upgrade'}</td>
                        <td>{request.requested_on ? new Date(request.requested_on).toLocaleString() : '-'}</td>
                        <td><span className="status-pill inactive">{request.status || 'pending'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>
          ) : null}

          {activeTab === 'alerts' ? (
            <article className="panel workspace-page admin-workspace-page">
              <div className="workspace-page-header">
                <div>
                  <h2>Daily Reminder Centre</h2>
                  <p>Choose a date, prepare its scheduled reminders, review them by outlet, and send the approved emails together.</p>
                </div>
                <div className="section-header-actions alert-queue-actions admin-alert-controls">
                  <label className="alert-date-picker">
                    Reminder date
                    <input type="date" value={alertDate} onChange={(event) => { setAlertDate(event.target.value); setSelectedAlertIds([]); }} />
                  </label>
                  <select className="outlet-filter" value={alertOutletFilter} onChange={(event) => setAlertOutletFilter(event.target.value)} aria-label="Filter by outlet">
                    <option value="ALL">All outlets</option>
                    {outlets.map((outlet) => <option key={outlet.id} value={String(outlet.id)}>{outlet.name || 'Outlet'}</option>)}
                  </select>
                  <select className="outlet-filter" value={alertStageFilter} onChange={(event) => setAlertStageFilter(event.target.value)} aria-label="Filter by reminder stage">
                    <option value="ALL">All email stages</option>
                    {Object.entries(ALERT_TYPE_LABELS).filter(([key]) => key.startsWith('automated_')).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                  </select>
                  {superadminReminderControlEnabled ? (
                    <button className="btn-link btn-alert" type="button" onClick={runAutomatedFetch} disabled={actionStatus === 'loading'}>
                      <RefreshOutlinedIcon fontSize="small" />
                      {actionStatus === 'loading' ? 'Preparing...' : 'Prepare reminders'}
                    </button>
                  ) : null}
                  <button className="btn-link btn-save" type="button" disabled={!selectedAlertIds.length || actionStatus === 'loading'} onClick={sendSelectedAlerts}>
                    <NotificationsActiveOutlinedIcon fontSize="small" />
                    Send selected ({selectedAlertIds.length})
                  </button>
                </div>
              </div>

              <section className="workspace-stats-grid" aria-label="Reminder summary">
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__icon"><NotificationsActiveOutlinedIcon fontSize="small" /></span>
                  <strong>{filteredAlertQueue.length}</strong>
                  <span>Ready for {alertDate}</span>
                </article>
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__dot is-active" />
                  <strong>{selectedAlertIds.length}</strong>
                  <span>Selected to send</span>
                </article>
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__dot is-inactive" />
                  <strong>{scheduledOutletCount}</strong>
                  <span>Outlets represented</span>
                </article>
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__icon is-purple"><NotificationsNoneOutlinedIcon fontSize="small" /></span>
                  <strong>{automatedQueuedAlertCount}</strong>
                  <span>Total reminders in queue</span>
                </article>
              </section>

              <div className="table-wrap workspace-table-card admin-alerts-table">
                <table className="pro-table mobile-cards table-admin-alert-queue">
                  <thead>
                    <tr>
                      <th><input type="checkbox" aria-label="Select all queued emails" checked={queuedVisibleAlertIds.length > 0 && queuedVisibleAlertIds.every((id) => selectedAlertIds.includes(id))} onChange={toggleSelectAllAlerts} /></th>
                      <th>Scheduled For</th>
                      <th>Status</th>
                      <th>Stage</th>
                      <th>Member</th>
                      <th>Outlet</th>
                      <th>Due Date</th>
                      <th>Message</th>
                      <th>Processed At</th>
                      <th>Email status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAlertQueue.length === 0 ? (
                      <tr><td colSpan="10">No emails found for this filter.</td></tr>
                    ) : (
                      filteredAlertQueue.map((alert) => (
                        <tr key={alert.id}>
                          <td><input type="checkbox" aria-label={`Select email for ${alert.member_name || 'member'}`} disabled={String(alert.delivery_status || '').toLowerCase() !== 'queued'} checked={selectedAlertIds.includes(alert.id)} onChange={() => toggleSelectedAlert(alert.id)} /></td>
                          <td>{(alert.scheduled_for || alert.queued_at) ? new Date(alert.scheduled_for || alert.queued_at).toLocaleString() : '-'}</td>
                          <td>
                            <span className={`status-pill ${getStatusKind(alert.delivery_status)}`}>{alert.delivery_status || '-'}</span>
                          </td>
                          <td>{formatAlertType(alert.alert_type)}</td>
                          <td>{alert.member_name || '-'}</td>
                          <td>{alert.outlet_name || '-'}</td>
                          <td>{alert.due_date ? new Date(alert.due_date).toLocaleDateString() : '-'}</td>
                          <td style={{ maxWidth: 320, whiteSpace: 'pre-wrap' }}>{alert.message_text || '-'}</td>
                          <td>{alert.processed_at ? new Date(alert.processed_at).toLocaleString() : '-'}</td>
                          <td><span className="muted">{String(alert.delivery_status || '').toLowerCase() === 'queued' ? 'Ready to send' : (alert.processed_by_name || alert.delivery_status || '-')}</span></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <div className="workspace-table-footer">
                  <span>Showing {filteredAlertQueue.length} ready-to-send reminders for {alertDate}</span>
                  {automatedAlertFetchSummary?.target_date ? (
                    <span>Last fetch: {automatedAlertFetchSummary.created || 0} queued for {automatedAlertFetchSummary.target_date}</span>
                  ) : null}
                  <span className="workspace-table-footer__pager">1</span>
                </div>
              </div>
            </article>
          ) : null}

          {activeTab === 'settings' ? (
            <article className="panel workspace-page admin-workspace-page">
              <form onSubmit={saveSettings}>
                <div className="workspace-page-header">
                  <div>
                    <h2>Settings</h2>
                    <p>Configure plan pricing and Growth reminder email settings.</p>
                  </div>
                  <div className="section-header-actions">
                    <button className="btn-link btn-save" type="submit" disabled={actionStatus === 'loading'}>
                      <SaveOutlinedIcon fontSize="small" />
                      {actionStatus === 'loading' ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </div>

                <section className="settings-panel-grid">
                  <article className="settings-card">
                    <h3>Subscription Charges</h3>
                    <div className="form-grid compact">
                      {['starter', 'growth'].map((tier) => (
                        <label key={tier}>
                          {tier.charAt(0).toUpperCase() + tier.slice(1)} Monthly Charge
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={settingsDraft.plan_charges?.[tier] ?? ''}
                            onChange={(event) => updatePlanChargeDraft(tier, event.target.value)}
                          />
                        </label>
                      ))}
                    </div>
                  </article>

                </section>

                <section className="settings-card settings-card--wide">
                  <h3>Growth Alert Schedule</h3>
                  <div className="settings-schedule-grid">
                    {['growth'].map((tier) => (
                      <article key={tier} className="settings-schedule-card">
                        <div className="settings-schedule-card__header">
                          <strong>Growth Automated Email Alerts</strong>
                          <span>{(settingsDraft.alert_schedules?.[tier]?.stages || []).filter((stage) => stage.enabled !== false).length} active alerts</span>
                        </div>
                        <div className="settings-alert-list">
                          {(settingsDraft.alert_schedules?.[tier]?.stages || []).map((stage, index) => (
                            <div key={stage.type} className="settings-alert-row">
                              <label className="checkline settings-alert-row__enabled">
                                <input
                                  type="checkbox"
                                  checked={stage.enabled !== false}
                                  onChange={(event) => updateScheduleStageDraft(tier, index, 'enabled', event.target.checked)}
                                />
                                Enabled
                              </label>
                              <label>
                                Alert Name
                                <input
                                  value={stage.label || ''}
                                  onChange={(event) => updateScheduleStageDraft(tier, index, 'label', event.target.value)}
                                />
                              </label>
                              <label>
                                When
                                <select
                                  value={stage.bucket || 'before_due'}
                                  onChange={(event) => updateScheduleStageDraft(tier, index, 'bucket', event.target.value)}
                                >
                                  <option value="before_due">Days before due date</option>
                                  <option value="on_due">On due date</option>
                                  <option value="after_due">Days after due date</option>
                                  <option value="cancellation">Disable alert after due date</option>
                                </select>
                              </label>
                              <label>
                                Days
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  disabled={stage.bucket === 'on_due'}
                                  value={stage.bucket === 'on_due' ? '' : (stage.day_count ?? Math.abs(stage.offset_days || 0))}
                                  onChange={(event) => updateScheduleStageDraft(tier, index, 'day_count', event.target.value)}
                                  placeholder={stage.bucket === 'on_due' ? 'No days' : 'Days'}
                                />
                              </label>
                              <label className="settings-alert-row__template">
                                Template
                                <textarea
                                  className="text-area"
                                  value={settingsDraft.alert_templates?.[stage.type] || ''}
                                  onChange={(event) => updateAlertTemplateDraft(stage.type, event.target.value)}
                                />
                              </label>
                            </div>
                          ))}
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              </form>
            </article>
          ) : null}

          {outletModal.type ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>{outletModal.title}</h2>
                {outletModal.type === 'create' ? (
                  <>
                    <StepTabs steps={[1, 2]} current={outletStep} />
                    {outletStep === 1 ? (
                      <form className="form-grid compact" onSubmit={submitOutletModal}>
                        <label>
                          Outlet Name
                          <input
                            required
                            value={outletForm.name}
                            onChange={(event) => setOutletForm((prev) => ({ ...prev, name: event.target.value }))}
                          />
                        </label>
                        <label>
                          Partner
                          <select
                            className="outlet-filter"
                            required
                            value={outletForm.associated_partner}
                            onChange={(event) => setOutletForm((prev) => ({ ...prev, associated_partner: event.target.value }))}
                          >
                            <option value="">Select Partner</option>
                            {partners.filter((partner) => partner.is_active).map((partner) => (
                              <option key={partner.id} value={partner.id}>
                                {partner.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Country
                          <select
                            required
                            value={outletForm.country}
                            onChange={(event) =>
                              setOutletForm((prev) => ({ ...prev, country: event.target.value, state: '', city: '' }))
                            }
                          >
                            <option value="">Select Country</option>
                            {Object.keys(COUNTRY_CONFIGS).map((country) => (
                              <option key={country} value={country}>{country}</option>
                            ))}
                            <option value="Other">Other</option>
                          </select>
                        </label>
                        <label>
                          State
                          <input
                            required
                            list="outlet-state-options"
                            value={outletForm.state}
                            onChange={(event) => setOutletForm((prev) => ({ ...prev, state: event.target.value }))}
                            placeholder={outletForm.country ? 'Select or type state' : 'Select country first'}
                            disabled={!outletForm.country}
                          />
                        </label>
                        <label>
                          City
                          <input
                            required
                            list="outlet-city-options"
                            value={outletForm.city}
                            onChange={(event) => setOutletForm((prev) => ({ ...prev, city: event.target.value }))}
                            placeholder={outletForm.state ? 'Select or type city' : 'Select state first'}
                            disabled={!outletForm.state}
                          />
                        </label>
                        <datalist id="outlet-state-options">
                          {outletStateOptions.map((state) => (
                            <option key={state} value={state} />
                          ))}
                        </datalist>
                        <datalist id="outlet-city-options">
                          {outletCityOptions.map((city) => (
                            <option key={city} value={city} />
                          ))}
                        </datalist>
                        <label>
                          Pincode
                          <input
                            required
                            value={outletForm.pincode}
                            onChange={(event) => setOutletForm((prev) => ({ ...prev, pincode: event.target.value }))}
                            placeholder={outletCountryConfig?.pinLength ? `${outletCountryConfig.pinLength} digits` : 'Pin code'}
                          />
                        </label>
                        <label>
                          Logo Upload
                          <input type="file" accept="image/*" onChange={(event) => handleLogoUpload(event.target.files?.[0])} />
                        </label>
                        {outletLogoPreview ? (
                          <img
                            src={outletLogoPreview}
                            alt="Outlet logo preview"
                            style={{ maxWidth: '160px', borderRadius: '12px' }}
                          />
                        ) : outletForm.logo ? (
                          <img
                            src={formatDocUrl(outletForm.logo)}
                            alt="Outlet logo preview"
                            style={{ maxWidth: '160px', borderRadius: '12px' }}
                          />
                        ) : null}
                        <label style={{ gridColumn: '1 / -1' }}>
                          Address
                          <input
                            required
                            value={outletForm.address}
                            onChange={(event) => setOutletForm((prev) => ({ ...prev, address: event.target.value }))}
                          />
                        </label>
                        <div className="row-actions">
                          <button className="btn-link btn-save" type="submit" disabled={actionStatus === 'loading'}>
                            <SaveOutlinedIcon fontSize="small" />
                            {actionStatus === 'loading' ? 'Saving...' : 'Continue'}
                          </button>
                          <button className="btn-link btn-cancel" type="button" onClick={closeOutletModal}>
                            <CloseOutlinedIcon fontSize="small" />
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <form className="form-grid compact" onSubmit={submitOutletModal}>
                        <label>
                          Document Type
                          <input
                            value={outletDocType}
                            onChange={(event) => setOutletDocType(event.target.value)}
                            disabled={skipOutletDocumentUpload}
                          />
                        </label>
                        <label className="checkline" style={{ gridColumn: '1 / -1' }}>
                          <input
                            type="checkbox"
                            checked={skipOutletDocumentUpload}
                            onChange={(event) => {
                              setSkipOutletDocumentUpload(event.target.checked);
                              if (event.target.checked) {
                                setOutletDocuments([]);
                              }
                            }}
                          />
                          Upload later
                        </label>
                        <label>
                          Upload Outlet Documents
                          <input
                            key={skipOutletDocumentUpload ? 'skip-outlet-documents' : 'upload-outlet-documents'}
                            type="file"
                            multiple
                            required={!skipOutletDocumentUpload}
                            disabled={skipOutletDocumentUpload}
                            onChange={(event) => setOutletDocuments(Array.from(event.target.files || []))}
                          />
                        </label>
                        <div className="row-actions">
                          <button className="btn-link btn-save" type="submit" disabled={actionStatus === 'loading'}>
                            <FileUploadOutlinedIcon fontSize="small" />
                            {actionStatus === 'loading' ? 'Uploading...' : skipOutletDocumentUpload ? 'Skip & Finish' : 'Finish'}
                          </button>
                          <button className="btn-link btn-cancel" type="button" onClick={closeOutletModal}>
                            <CloseOutlinedIcon fontSize="small" />
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </>
                ) : outletModal.type === 'edit' ? (
                  <>
                    <StepTabs steps={[1, 2]} current={outletEditStep} onStepChange={setOutletEditStep} />
                    {outletEditStep === 1 ? (
                      <form className="form-grid compact" onSubmit={submitOutletModal}>
                        <label>
                          Outlet Name
                          <input
                            required
                            value={outletForm.name}
                            onChange={(event) => setOutletForm((prev) => ({ ...prev, name: event.target.value }))}
                          />
                        </label>
                        <label>
                          Partner
                          <select
                            className="outlet-filter"
                            required
                            value={outletForm.associated_partner}
                            onChange={(event) => setOutletForm((prev) => ({ ...prev, associated_partner: event.target.value }))}
                          >
                            <option value="">Select Partner</option>
                            {partners.filter((partner) => partner.is_active).map((partner) => (
                              <option key={partner.id} value={partner.id}>
                                {partner.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Country
                          <select
                            required
                            value={outletForm.country}
                            onChange={(event) =>
                              setOutletForm((prev) => ({ ...prev, country: event.target.value, state: '', city: '' }))
                            }
                          >
                            <option value="">Select Country</option>
                            {Object.keys(COUNTRY_CONFIGS).map((country) => (
                              <option key={country} value={country}>{country}</option>
                            ))}
                            <option value="Other">Other</option>
                          </select>
                        </label>
                        <label>
                          State
                          <input
                            required
                            list="outlet-state-options"
                            value={outletForm.state}
                            onChange={(event) => setOutletForm((prev) => ({ ...prev, state: event.target.value }))}
                            placeholder={outletForm.country ? 'Select or type state' : 'Select country first'}
                            disabled={!outletForm.country}
                          />
                        </label>
                        <label>
                          City
                          <input
                            required
                            list="outlet-city-options"
                            value={outletForm.city}
                            onChange={(event) => setOutletForm((prev) => ({ ...prev, city: event.target.value }))}
                            placeholder={outletForm.state ? 'Select or type city' : 'Select state first'}
                            disabled={!outletForm.state}
                          />
                        </label>
                        <datalist id="outlet-state-options">
                          {outletStateOptions.map((state) => (
                            <option key={state} value={state} />
                          ))}
                        </datalist>
                        <datalist id="outlet-city-options">
                          {outletCityOptions.map((city) => (
                            <option key={city} value={city} />
                          ))}
                        </datalist>
                        <label>
                          Pincode
                          <input
                            required
                            value={outletForm.pincode}
                            onChange={(event) => setOutletForm((prev) => ({ ...prev, pincode: event.target.value }))}
                            placeholder={outletCountryConfig?.pinLength ? `${outletCountryConfig.pinLength} digits` : 'Pin code'}
                          />
                        </label>
                        <label>
                          Logo Upload
                          <input type="file" accept="image/*" onChange={(event) => handleLogoUpload(event.target.files?.[0])} />
                        </label>
                        {outletLogoPreview ? (
                          <img
                            src={outletLogoPreview}
                            alt="Outlet logo preview"
                            style={{ maxWidth: '160px', borderRadius: '12px' }}
                          />
                        ) : outletForm.logo ? (
                          <img
                            src={formatDocUrl(outletForm.logo)}
                            alt="Outlet logo preview"
                            style={{ maxWidth: '160px', borderRadius: '12px' }}
                          />
                        ) : null}
                        <label style={{ gridColumn: '1 / -1' }}>
                          Address
                          <input
                            required
                            value={outletForm.address}
                            onChange={(event) => setOutletForm((prev) => ({ ...prev, address: event.target.value }))}
                          />
                        </label>
                        <div className="row-actions">
                          <button className="btn-link btn-save" type="submit" disabled={actionStatus === 'loading'}>
                            <SaveOutlinedIcon fontSize="small" />
                            {actionStatus === 'loading' ? 'Saving...' : 'Save Details'}
                          </button>
                          <button className="btn-link btn-cancel" type="button" onClick={closeOutletModal}>
                            <CloseOutlinedIcon fontSize="small" />
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <form className="form-grid compact" onSubmit={submitOutletModal}>
                        <label>
                          Document Type
                          <input
                            value={outletDocType}
                            onChange={(event) => setOutletDocType(event.target.value)}
                            disabled={skipOutletDocumentUpload}
                          />
                        </label>
                        {outletModal.outlet?.document_path ? (
                          <a className="btn-link btn-export mini" href={formatDocUrl(outletModal.outlet.document_path)} target="_blank" rel="noreferrer">
                            <FileDownloadOutlinedIcon fontSize="small" />
                            Download Existing Document
                          </a>
                        ) : null}
                        <label className="checkline" style={{ gridColumn: '1 / -1' }}>
                          <input
                            type="checkbox"
                            checked={skipOutletDocumentUpload}
                            onChange={(event) => {
                              setSkipOutletDocumentUpload(event.target.checked);
                              if (event.target.checked) {
                                setOutletDocuments([]);
                              }
                            }}
                          />
                          Upload later
                        </label>
                        <label>
                          Upload Outlet Documents
                          <input
                            key={skipOutletDocumentUpload ? 'skip-edit-outlet-documents' : 'upload-edit-outlet-documents'}
                            type="file"
                            multiple
                            required={!skipOutletDocumentUpload}
                            disabled={skipOutletDocumentUpload}
                            onChange={(event) => setOutletDocuments(Array.from(event.target.files || []))}
                          />
                        </label>
                        <div className="row-actions">
                          <button className="btn-link btn-save" type="submit" disabled={actionStatus === 'loading'}>
                            <FileUploadOutlinedIcon fontSize="small" />
                            {actionStatus === 'loading' ? 'Uploading...' : skipOutletDocumentUpload ? 'Skip & Finish' : 'Upload Documents'}
                          </button>
                          <button className="btn-link btn-cancel" type="button" onClick={closeOutletModal}>
                            <CloseOutlinedIcon fontSize="small" />
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </>
                ) : (
                  <div className="form-grid">
                    <p className="muted">
                      {outletModal.type === 'toggle'
                        ? `Are you sure you want to ${outletModal.outlet?.is_active ? 'disable' : 'enable'} outlet "${outletModal.outlet?.name}"?`
                        : `Are you sure you want to delete outlet "${outletModal.outlet?.name}"?`}
                    </p>
                    <div className="row-actions">
                      <button className={`btn-link ${outletModal.type === 'delete' ? 'btn-delete' : outletModal.outlet?.is_active ? 'btn-disable' : 'btn-enable'}`} type="button" onClick={submitOutletModal} disabled={actionStatus === 'loading'}>
                        {outletModal.type === 'delete' ? <DeleteOutlineOutlinedIcon fontSize="small" /> : outletModal.outlet?.is_active ? <BlockOutlinedIcon fontSize="small" /> : <CheckCircleOutlineOutlinedIcon fontSize="small" />}
                        {actionStatus === 'loading' ? 'Processing...' : 'Confirm'}
                      </button>
                      <button className="btn-link btn-cancel" type="button" onClick={closeOutletModal}>
                        <CloseOutlinedIcon fontSize="small" />
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </article>
            </div>
          ) : null}

          {showOnboarding ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>Partner Onboarding</h2>
                <StepTabs steps={[1, 2, 3]} current={step} />
                {step === 1 ? (
                  <form className="form-grid compact" onSubmit={submitStepOne}>
                    <label>Name<input required maxLength={20} value={partnerForm.name} onChange={(event) => setPartnerForm((prev) => ({ ...prev, name: event.target.value }))} /></label>
                    <label>Email<input required={emailVerificationRequired} type="email" value={partnerForm.email} onChange={(event) => setPartnerForm((prev) => ({ ...prev, email: event.target.value }))} /></label>
                    <label>
                      Country
                      <select required value={partnerForm.country} onChange={(event) => setPartnerForm((prev) => ({ ...prev, country: event.target.value, state: '', city: '' }))}>
                        <option value="">Select Country</option>
                        {Object.keys(COUNTRY_CONFIGS).map((country) => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                        <option value="Other">Other</option>
                      </select>
                    </label>
                    <label>
                      Mobile {partnerCountryConfig?.dialCode ? `(${partnerCountryConfig.dialCode})` : ''}
                      <input
                        required
                        inputMode="numeric"
                        value={partnerForm.mobile}
                        onChange={(event) => setPartnerForm((prev) => ({ ...prev, mobile: event.target.value }))}
                        placeholder={partnerCountryConfig?.mobileLength ? `${partnerCountryConfig.mobileLength} digits` : 'Mobile number'}
                        disabled={!partnerForm.country}
                        maxLength={partnerCountryConfig?.mobileLength || undefined}
                      />
                    </label>
                    <label>UPI ID<input value={partnerForm.upi_id} onChange={(event) => setPartnerForm((prev) => ({ ...prev, upi_id: event.target.value }))} placeholder="name@bank" /></label>
                    <label>Password<input required type="password" maxLength={72} value={partnerForm.password} onChange={(event) => setPartnerForm((prev) => ({ ...prev, password: event.target.value }))} /></label>
                    <label>
                      State
                      <input
                        list="partner-state-options"
                        value={partnerForm.state}
                        onChange={(event) => setPartnerForm((prev) => ({ ...prev, state: event.target.value }))}
                        placeholder={partnerForm.country ? 'Select or type state' : 'Select country first'}
                        disabled={!partnerForm.country}
                      />
                    </label>
                    <datalist id="partner-state-options">
                      {partnerStateOptions.map((state) => (
                        <option key={state} value={state} />
                      ))}
                    </datalist>
                    <label>
                      City
                      <input
                        list="partner-city-options"
                        value={partnerForm.city}
                        onChange={(event) => setPartnerForm((prev) => ({ ...prev, city: event.target.value }))}
                        placeholder={partnerForm.state ? 'Select or type city' : 'Select state first'}
                        disabled={!partnerForm.state}
                      />
                    </label>
                    <datalist id="partner-city-options">
                      {partnerCityOptions.map((city) => (
                        <option key={city} value={city} />
                      ))}
                    </datalist>
                    <label>
                      Pin Code
                      <input
                        value={partnerForm.pin_code}
                        onChange={(event) => setPartnerForm((prev) => ({ ...prev, pin_code: event.target.value }))}
                        placeholder={partnerCountryConfig?.pinLength ? `${partnerCountryConfig.pinLength} digits` : 'Pin code'}
                        required
                      />
                    </label>
                    <label style={{ gridColumn: '1 / -1' }}>Address<input required value={partnerForm.address} onChange={(event) => setPartnerForm((prev) => ({ ...prev, address: event.target.value }))} /></label>
                    <div className="row-actions">
                      <button className="btn-link btn-primary-action" type="submit" disabled={actionStatus === 'loading'}><CheckCircleOutlineOutlinedIcon fontSize="small" />{actionStatus === 'loading' ? 'Submitting...' : 'Continue'}</button>
                      <button className="btn-link btn-cancel" type="button" onClick={cancelOnboarding}><CloseOutlinedIcon fontSize="small" />Cancel</button>
                    </div>
                  </form>
                ) : null}

                {step === 2 ? (
                  <div className="form-grid compact">
                    <label>Email OTP<input value={otp.email} onChange={(event) => setOtp((prev) => ({ ...prev, email: event.target.value }))} placeholder="Enter email OTP" /></label>
                    <button className="btn-link btn-save" type="button" disabled={verified.email || actionStatus === 'loading'} onClick={() => submitOtp('email')}>
                      <CheckCircleOutlineOutlinedIcon fontSize="small" />
                      {verified.email ? 'Email Verified' : 'Verify Email'}
                    </button>
                    <div className="row-actions">
                      <button className="btn-link btn-primary-action" type="button" onClick={() => setStep(3)}><CheckCircleOutlineOutlinedIcon fontSize="small" />Verify Later</button>
                      <button className="btn-link btn-cancel" type="button" onClick={cancelOnboarding}><CloseOutlinedIcon fontSize="small" />Cancel</button>
                    </div>
                  </div>
                ) : null}

                {step === 3 ? (
                  <form className="form-grid compact" onSubmit={submitStepThree}>
                    <label>Document Type<input value={docType} disabled={skipPartnerDocumentUpload} onChange={(event) => setDocType(event.target.value)} /></label>
                    <label className="checkline" style={{ gridColumn: '1 / -1' }}>
                      <input
                        type="checkbox"
                        checked={skipPartnerDocumentUpload}
                        onChange={(event) => {
                          setSkipPartnerDocumentUpload(event.target.checked);
                          if (event.target.checked) {
                            setDocuments([]);
                          }
                        }}
                      />
                      Upload later
                    </label>
                    <label>
                      Upload Documents
                      <input
                        key={skipPartnerDocumentUpload ? 'skip-partner-documents' : 'upload-partner-documents'}
                        type="file"
                        multiple
                        required={!skipPartnerDocumentUpload}
                        disabled={skipPartnerDocumentUpload}
                        onChange={(event) => setDocuments(Array.from(event.target.files || []))}
                      />
                    </label>
                    <div className="row-actions">
                      <button className="btn-link btn-save" type="submit" disabled={actionStatus === 'loading'}><FileUploadOutlinedIcon fontSize="small" />{actionStatus === 'loading' ? 'Uploading...' : skipPartnerDocumentUpload ? 'Skip & Finish' : 'Finish Onboarding'}</button>
                      <button className="btn-link btn-cancel" type="button" onClick={cancelOnboarding}><CloseOutlinedIcon fontSize="small" />Cancel</button>
                    </div>
                  </form>
                ) : null}
              </article>
            </div>
          ) : null}

          {editingPartner ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>Edit Partner</h2>
                <StepTabs steps={[1, 2]} current={editingPartnerStep} onStepChange={setEditingPartnerStep} />
                {editingPartnerStep === 1 ? (
                  <form className="form-grid compact" onSubmit={submitEdit}>
                    <label>Name<input maxLength={20} value={editingPartner.name} onChange={(event) => setEditingPartner((prev) => ({ ...prev, name: event.target.value }))} /></label>
                    <label>Email<input type="email" value={editingPartner.email} onChange={(event) => setEditingPartner((prev) => ({ ...prev, email: event.target.value }))} /></label>
                    <label>Mobile<input value={editingPartner.mobile} onChange={(event) => setEditingPartner((prev) => ({ ...prev, mobile: event.target.value }))} /></label>
                    <div className="row-actions">
                      <button className="btn-link btn-save" type="submit" disabled={actionStatus === 'loading'}>
                        <SaveOutlinedIcon fontSize="small" />
                        {actionStatus === 'loading'
                          ? 'Saving...'
                          : 'Save Details'}
                      </button>
                      <button className="btn-link btn-cancel" type="button" onClick={() => setEditingPartner(null)}><CloseOutlinedIcon fontSize="small" />Cancel</button>
                    </div>
                  </form>
                ) : (
                  <form className="form-grid compact" onSubmit={submitEditPartnerDocuments}>
                    <label>Document Type<input value={editingPartnerDocType} onChange={(event) => setEditingPartnerDocType(event.target.value)} /></label>
                    {editingPartner?.document_path ? (
                      <a className="btn-link btn-export mini" href={formatDocUrl(editingPartner.document_path)} target="_blank" rel="noreferrer">
                        <FileDownloadOutlinedIcon fontSize="small" />
                        Download Existing Document
                      </a>
                    ) : null}
                    <label>Upload Documents<input type="file" multiple required onChange={(event) => setEditingPartnerDocuments(Array.from(event.target.files || []))} /></label>
                    <div className="row-actions">
                      <button className="btn-link btn-save" type="submit" disabled={actionStatus === 'loading'}><FileUploadOutlinedIcon fontSize="small" />{actionStatus === 'loading' ? 'Uploading...' : 'Upload Documents'}</button>
                      <button className="btn-link btn-cancel" type="button" onClick={() => setEditingPartner(null)}><CloseOutlinedIcon fontSize="small" />Cancel</button>
                    </div>
                  </form>
                )}
              </article>
            </div>
          ) : null}
        </section>
      </section>
    </main>
  );
}

export default SuperAdminDashboard;
